/**
 * Phase 21.4 iter_184 — Contest Maintenance & Backfill Section.
 *
 * Permanent admin-only surface at the bottom of /admin/control-center.
 * Every backfill is idempotent — safe to run multiple times.
 *
 * UX contract:
 *   1. Load preview (dry-run) on mount so admin sees the missing count
 *      before doing anything.
 *   2. Each row has: icon, name, preview count, "Preview" and "Run" buttons.
 *   3. Running shows inline progress + final summary
 *      (scanned / repaired / skipped / errors).
 *   4. All buttons disabled while ANY backfill is running to prevent
 *      concurrent writes.
 *
 * Do not couple to Live / Memberships / Stripe / Wallets. Reads and
 * writes are strictly on contests / contest_entries / hall_of_champions
 * / coin_rewards.
 */
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import api from "@/lib/api";
import { toast } from "sonner";

const BACKFILLS = [
  { id: "covers", key: "covers", icon: "🎨", label: "Backfill Winner Covers",
    endpoint: "/admin/contests/backfill/covers",
    desc: "Generate missing 1500×1500 shareable covers for contest winners." },
  { id: "hall_of_champions", key: "hall_of_champions", icon: "🏆", label: "Backfill Hall of Champions",
    endpoint: "/admin/contests/backfill/hall-of-champions",
    desc: "Mirror winner entries into the Hall of Champions archive." },
  { id: "badges", key: "badges", icon: "🎖", label: "Backfill Contest Badges",
    endpoint: "/admin/contests/backfill/badges",
    desc: "Attach placement badges to winners whose profile / source is missing them." },
  { id: "rewards", key: "rewards", icon: "💰", label: "Backfill Contest Rewards",
    endpoint: "/admin/contests/backfill/rewards",
    desc: "Re-run reward pipeline (coins/promo) for winners whose audit rows are absent. Idempotent." },
  { id: "records", key: "records", icon: "📜", label: "Backfill Historical Contest Records",
    endpoint: "/admin/contests/backfill/records",
    desc: "Sync the display winners[] array on each closed contest doc + stamp results_at." },
];

const StatPill = ({ label, value, tone = "gray", testId }) => {
  const toneCls = {
    gray: "text-gray-300",
    green: "text-emerald-300",
    amber: "text-amber-200",
    rose: "text-rose-300",
  }[tone] || "text-gray-300";
  return (
    <div className="bg-white/[0.03] rounded px-2 py-1" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-wider text-gray-500">{label}</p>
      <p className={`text-base font-black ${toneCls}`}>{value ?? "—"}</p>
    </div>
  );
};

const BackfillRow = ({ item, preview, result, running, busy, onPreview, onRun }) => {
  const p = preview || {};
  const r = result || {};
  const pending = (p.repaired || 0);
  return (
    <div
      className="rounded-lg border border-white/10 bg-white/[0.02] p-4"
      data-testid={`admin-backfill-row-${item.id}`}
    >
      <div className="flex flex-wrap items-start gap-3 justify-between">
        <div className="min-w-0">
          <p className="text-base font-black text-white">
            <span className="mr-1.5">{item.icon}</span>
            {item.label}
          </p>
          <p className="text-[12px] text-gray-400 mt-0.5 max-w-xl">{item.desc}</p>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={onPreview}
            data-testid={`admin-backfill-preview-${item.id}`}
            className="text-purple-100 border-purple-500/40"
          >
            {running === "preview" ? "..." : "🔍 Preview"}
          </Button>
          <Button
            size="sm"
            disabled={busy}
            onClick={onRun}
            data-testid={`admin-backfill-run-${item.id}`}
            className="bg-amber-500 hover:bg-amber-400 text-black font-black"
          >
            {running === "commit" ? "Running…" : "▶ Run"}
          </Button>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
        <StatPill
          label="Pending (preview)"
          value={running === "preview" ? "…" : (pending || 0)}
          tone={pending > 0 ? "amber" : "gray"}
          testId={`admin-backfill-pending-${item.id}`}
        />
        <StatPill
          label="Scanned"
          value={r.scanned ?? "—"}
          testId={`admin-backfill-scanned-${item.id}`}
        />
        <StatPill
          label="Repaired"
          value={r.repaired ?? "—"}
          tone={r.repaired ? "green" : "gray"}
          testId={`admin-backfill-repaired-${item.id}`}
        />
        <StatPill
          label="Errors"
          value={r.errors ? r.errors.length : "—"}
          tone={r.errors && r.errors.length ? "rose" : "gray"}
          testId={`admin-backfill-errors-${item.id}`}
        />
      </div>

      {r.sample && r.sample.length > 0 && (
        <details
          className="mt-2 text-[11px] text-gray-400"
          data-testid={`admin-backfill-sample-${item.id}`}
        >
          <summary className="cursor-pointer hover:text-white">
            View sample ({r.sample.length})
          </summary>
          <ul className="mt-1 space-y-0.5 font-mono">
            {r.sample.slice(0, 8).map((s, i) => (
              <li key={i} className="truncate">
                {Object.entries(s).map(([k, v]) => `${k}=${v}`).join(" · ")}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};

const AdminContestBackfillSection = () => {
  const [preview, setPreview] = useState(null);
  const [results, setResults] = useState({});
  const [runningId, setRunningId] = useState(null); // {id, mode}
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  const loadPreview = useCallback(async () => {
    setLoadingPreview(true);
    setPreviewError(null);
    try {
      const { data } = await api.get("/admin/contests/backfill/preview");
      setPreview(data);
    } catch (e) {
      setPreviewError(e?.response?.data?.detail || "Failed to load preview");
    } finally {
      setLoadingPreview(false);
    }
  }, []);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  const runOne = async (item, mode /* "preview" | "commit" */) => {
    setRunningId({ id: item.id, mode });
    try {
      const { data } = await api.post(item.endpoint, {
        dry_run: mode === "preview",
      });
      if (mode === "commit") {
        setResults((prev) => ({ ...prev, [item.key]: data }));
        toast.success(
          `${item.icon} ${item.label}: ${data.repaired || 0} repaired, ${data.skipped || 0} skipped`,
        );
        // Refresh preview so pending count updates.
        loadPreview();
      } else {
        setPreview((prev) => ({ ...(prev || {}), [item.key]: data }));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${item.label} failed`);
    } finally {
      setRunningId(null);
    }
  };

  const isBusy = !!runningId;

  return (
    <Card
      className="p-4 bg-white/[0.03] border-white/10"
      data-testid="admin-cc-backfill-section"
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-sm text-gray-400">
            Permanent, idempotent tools. Each backfill only touches missing state — safe to run repeatedly.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={loadPreview}
          disabled={loadingPreview || isBusy}
          data-testid="admin-backfill-refresh-preview"
          className="text-purple-100 border-purple-500/40"
        >
          {loadingPreview ? "…" : "↻ Refresh preview"}
        </Button>
      </div>

      {previewError && (
        <p
          className="text-rose-300 text-xs mb-2"
          data-testid="admin-backfill-preview-error"
        >
          {previewError}
        </p>
      )}

      <div className="grid grid-cols-1 gap-3">
        {BACKFILLS.map((item) => (
          <BackfillRow
            key={item.id}
            item={item}
            preview={preview ? preview[item.key] : null}
            result={results[item.key]}
            running={runningId?.id === item.id ? runningId.mode : null}
            busy={isBusy}
            onPreview={() => runOne(item, "preview")}
            onRun={() => runOne(item, "commit")}
          />
        ))}
      </div>
    </Card>
  );
};

export default AdminContestBackfillSection;
